import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import * as Sentry from '@sentry/react'
import _ from 'lodash'
import { ShieldAlert } from 'lucide-react'
import { v4 } from 'uuid'
import { useShallow } from 'zustand/react/shallow'

import { cn, ToBackendKeys } from 'utils/utils'

import Header from '../../components/Header'
import { Button } from '@harvey-frontend/components/ui/button'
import { RadioGroup } from '@harvey-frontend/components/ui/radio-group'
import LoadingScreen from 'components/common/loading-screen'

import { Playbook, StoredReviewResponse } from '../../../../schemas/playbook'
import { useFetchPlaybooks } from '../../hooks/useFetchPlaybooks'
import { useRunPlaybookReview } from '../../hooks/useRunPlaybookReview'
import useAppStore from '../../stores/store'
import { getDocumentAsPdfAsync } from '../../utils/office'
import { jumpToTextInDocument } from '../../utils/office'
import ErrorScreen from './ErrorScreen'
import { LoadingScreen as RunningLoadingScreen } from './LoadingScreen'

const MAX_PDF_CONVERSION_RETRIES = 3

export const PlaybookScreen = () => {
  const navigate = useNavigate()
  const { data: playbooks, isSuccess } = useFetchPlaybooks()
  const [documentPDF, setDocumentPDF] = useState<Blob | null>(null)
  const [pdfError, setPdfError] = useState<string | null>(null)
  const {
    documentId,
    playbook: storedPlaybook,
    setPlaybook,
    setReviewResult,
  } = useAppStore(
    useShallow((state) => ({
      setPlaybook: state.setPlaybook,
      setReviewResult: state.setReviewResult,
      documentId: state.documentId,
      playbook: state.playbook,
    }))
  )
  // "activePlaybook" is a playbook that's actively being reviewed.
  // "storedPlaybook" is the playbook with a stored review result.
  const [activePlaybook, setActivePlaybook] = useState<Playbook | null>(
    storedPlaybook
  )

  const {
    mutateAsync: runPlaybookReview,
    isPending,
    isError,
    error,
    reset,
    cancel,
    completion,
  } = useRunPlaybookReview(
    JSON.stringify(ToBackendKeys(activePlaybook?.rules || [])),
    documentId,
    {
      onSuccess: async (data) => {
        // Find the Word range in the document for each reference, and track it here.
        const transformedReviewItems = await Promise.all(
          data.review.map(async (reviewItem) => ({
            ...reviewItem,
            highlightedReferenceId: null,
            references: reviewItem.references
              ? await Promise.all(
                  reviewItem.references.map(async (reference) => {
                    const rangeId = v4()
                    try {
                      const rangeInDocument = await jumpToTextInDocument(
                        reference.text
                      )
                      // Expand the range to the whole paragraph.
                      const expandedRange = rangeInDocument.expandTo(
                        rangeInDocument.paragraphs
                          .getFirst()
                          .getRange(Word.RangeLocation.whole)
                      )
                      expandedRange.load('text')
                      expandedRange.track()
                      await expandedRange.context.sync()
                      rangeInDocument.untrack()
                      return {
                        ...reference,
                        range: expandedRange,
                        id: rangeId,
                        appliedSuggestionId: null,
                        suggestions: [],
                        originalText: expandedRange.text,
                      }
                    } catch (error) {
                      console.error(error)
                      Sentry.captureException(error)
                      return {
                        ...reference,
                        range: null,
                        id: rangeId,
                        appliedSuggestionId: null,
                        suggestions: [],
                        originalText: null,
                      }
                    }
                  })
                )
              : [],
          }))
        )
        const newReviewResult: StoredReviewResponse = {
          ...data,
          review: transformedReviewItems,
        }
        setReviewResult(newReviewResult)
        setPlaybook(activePlaybook)
        navigate('/playbook-review')
      },
      onError: (error) => {
        console.error(error)
      },
    }
  )

  // Define the function to attempt PDF conversion with retries
  const attemptGetDocumentAsPdf = useCallback(async (retryCount = 0) => {
    try {
      await getDocumentAsPdfAsync((pdf) => {
        setDocumentPDF(pdf)
        setPdfError(null)
      })
    } catch (error) {
      if (retryCount < MAX_PDF_CONVERSION_RETRIES - 1) {
        console.error(
          `PDF conversion failed, retrying (${retryCount + 1}/${MAX_PDF_CONVERSION_RETRIES})`
        )
        setTimeout(() => attemptGetDocumentAsPdf(retryCount + 1), 1000)
      } else {
        console.error(
          `PDF conversion failed after ${MAX_PDF_CONVERSION_RETRIES} attempts`
        )
        setPdfError(
          `Failed to convert document to PDF -- cannot run playbook review. Please try relaunching Word and trying again.`
        )
        Sentry.captureException(error)
      }
    }
  }, [])

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    attemptGetDocumentAsPdf()
  }, [attemptGetDocumentAsPdf])

  // Handler for retry button on error screen
  const handleRetryPdfConversion = () => {
    setPdfError(null)
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    attemptGetDocumentAsPdf()
  }

  if (isError) {
    return <ErrorScreen retry={() => reset()} error={error.message} />
  }

  if (pdfError) {
    return <ErrorScreen retry={handleRetryPdfConversion} error={pdfError} />
  }

  if (!isSuccess) {
    return <LoadingScreen />
  }

  const playbookWithDataIdx = playbooks.findIndex((localPlaybook) =>
    _.isEqual(storedPlaybook, localPlaybook)
  )

  const handleSubmit = async () => {
    if (!documentPDF) {
      Sentry.captureException('No document PDF found on playbook submit.')
      return
    }
    await runPlaybookReview(documentPDF)
  }

  const handleViewResults = () => {
    navigate('/playbook-review')
  }

  const warningText =
    playbooks.length > 0
      ? 'These files are managed by your Harvey workspace admins.'
      : 'You do not have any playbooks available. Please contact your admin for further assistance.'

  if (isPending) {
    return (
      <div className="flex h-screen flex-col bg-secondary">
        <Header title="Running playbook" isDisabled showDisabled={false} />
        <RunningLoadingScreen
          cancel={cancel}
          numRules={activePlaybook?.rules.length || 0}
          completionStatus={completion}
        />
      </div>
    )
  }

  return (
    <div className="flex h-screen flex-col bg-primary">
      <Header title="Choose playbook" to="/app-selection" />
      <div className="m-4 flex items-center gap-2 rounded-lg bg-secondary-hover p-2">
        <ShieldAlert className="h-4 w-4 shrink-0" />
        <p className="text-sm">{warningText}</p>
      </div>

      <div className="flex-1 px-4">
        <RadioGroup className="flex flex-col gap-0">
          {playbooks.map((playbook, index) => {
            const isSelected = playbookWithDataIdx === index

            return (
              <div
                className={cn(
                  'flex w-full items-center gap-2 px-2 py-2.5 hover:bg-secondary-hover',
                  {
                    'bg-secondary': isSelected,
                    'border-t': index > 0,
                    'border-b': index === playbooks.length - 1,
                    'cursor-not-allowed': isPending,
                  }
                )}
                key={index}
                role="button"
                aria-disabled={isPending}
                tabIndex={0}
              >
                <div className="flex w-full flex-col">
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">
                        {playbook.name}
                      </span>
                      <span className="text-xs text-muted">
                        {playbook.rules.length} rules
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {isSelected ? (
                        <>
                          <Button
                            onClick={async (e) => {
                              e.stopPropagation()
                              setActivePlaybook(playbook)
                              await handleSubmit()
                            }}
                            disabled={isPending}
                            size="sm"
                            variant="outline"
                          >
                            Re-run
                          </Button>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleViewResults()
                            }}
                            disabled={isPending}
                            size="sm"
                          >
                            View
                          </Button>
                        </>
                      ) : (
                        <Button
                          onClick={async (e) => {
                            e.stopPropagation()
                            setActivePlaybook(playbook)
                            await handleSubmit()
                          }}
                          disabled={isPending}
                          size="sm"
                          variant="outline"
                        >
                          Run
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </RadioGroup>
      </div>
    </div>
  )
}

export default PlaybookScreen
